import { Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import { JsonBlock, CardContent, Card, Button, Typography } from '@occc/ui-design-system';
import { snackbar } from '@occc-app/design-system';
import { useUpdateMembership } from '@occc-app/dataAccessLayer/membership/mutations';

import { CustomDataEditModal } from './custom-data-edit.modal';
import { CustomDataForm, MembershipCustomData } from './custom-data.types';
import { getCustomDataFormInitialValues } from './custom-data.utils';
import { CustomDataTable } from './custom-data-table.component';
import { errorMessage } from '../../copies';
import { checkCustomDataForConfigOverrides } from '../../utils';

type CustomDataContentProps = {
  data: MembershipCustomData;
  memberId: string;
  customerUuid?: string | null;
  canEdit: boolean;
};

export const CustomDataContent = ({
  data,
  memberId,
  customerUuid,
  canEdit,
}: CustomDataContentProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const customDataModalInitialValues = useMemo(() => getCustomDataFormInitialValues(data), [data]);
  const [updateMembership] = useUpdateMembership({
    refetchQueries: ['membership'],
  });

  const configOverrideEntries = useMemo(() => {
    if (!checkCustomDataForConfigOverrides(data)) {
      return null;
    }

    return Object.entries(data.configOverrides).map(([key, value]) => ({
      key,
      value,
    }));
  }, [data]);

  const handleCustomDataModalClose = () => {
    setIsEditModalOpen(false);
  };

  const handleCustomDataFormSubmit = async (values: CustomDataForm) => {
    try {
      const customDataInput = values.customData?.trim();
      const customDataValue = customDataInput ? JSON.parse(customDataInput) : null;

      await updateMembership({
        variables: {
          input: {
            // Some memberships may not have CustomerId.
            // Pass both, and the API will handle cases where one is missing.
            id: memberId,
            customerUuid: customerUuid ?? undefined,
            customData: customDataValue ? { data: customDataValue } : null,
          },
        },
      });

      snackbar.success('Custom data has been successfully updated');
      setIsEditModalOpen(false);
    } catch {
      snackbar.error(errorMessage);
    }
  };

  return (
    <Stack spacing={1}>
      {configOverrideEntries && <CustomDataTable items={configOverrideEntries} />}

      <Card>
        <CardContent>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='h6' component='h3'>
              Custom Data
            </Typography>
            {canEdit ? (
              <Button variant='contained' onClick={() => setIsEditModalOpen(true)}>
                Edit
              </Button>
            ) : null}
          </Stack>

          <JsonBlock object={data ?? {}} />
        </CardContent>
      </Card>

      <CustomDataEditModal
        isOpen={isEditModalOpen}
        initialValues={customDataModalInitialValues}
        handleClose={handleCustomDataModalClose}
        handleSubmit={handleCustomDataFormSubmit}
      />
    </Stack>
  );
};
